import { Box, List, ListItem, styled, Typography } from '@mui/material';
import React, { memo, useCallback } from 'react';

import { CustomFile } from '../../Types/file';
import FileCard from '../Chat/FileCard';
import FileDropzone from '../FileDropzone';

type PromptFileUploadProps = {
  customStyles?: React.CSSProperties;
  existingFiles?: CustomFile[];
  setExistingFiles: React.Dispatch<React.SetStateAction<CustomFile[]>>;
  onFileView?: (file: CustomFile) => void;
  uploadedFiles: File[];
  setUploadedFiles: React.Dispatch<React.SetStateAction<File[]>>;
};

const UploadTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  marginBottom: theme.spacing(1),
}));

const FileListContainer = styled(Box)(() => ({
  flex: 1,
  minWidth: '300px',
}));

const FileList = styled(List)(({ theme }) => ({
  marginTop: theme.spacing(1),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  width: '100%',
  gap: 1,
}));

const FileListItem = styled(ListItem)(() => ({
  width: '100%',
  maxWidth: 320,
  boxSizing: 'border-box',
}));

const FileCardWrapper = styled('div')(() => ({
  cursor: 'pointer',
}));

const FileListTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

const ExistingFilesList = ({
  files,
  onDelete,
  onView,
}: {
  files: CustomFile[];
  onDelete: (index: number) => void;
  onView?: (file: CustomFile) => void;
}) => {
  return (
    <FileList>
      {files.map((file, index) => (
        <FileListItem key={file.id || index}>
          <FileCardWrapper onClick={() => onView?.(file)}>
            <FileCard handleFileDelete={() => onDelete(index)} uploadedFile={file} />
          </FileCardWrapper>
        </FileListItem>
      ))}
    </FileList>
  );
};

export const UploadedFilesList = ({
  files,
  onDelete,
}: {
  files: File[];
  onDelete: (index: number) => void;
}) => {
  return (
    <FileList>
      {files.map((file, index) => (
        <FileListItem key={file.name + file.size + index}>
          <FileCard handleFileDelete={() => onDelete(index)} fileToUpload={file} />
        </FileListItem>
      ))}
    </FileList>
  );
};

const PromptFileUpload = ({
  customStyles,
  existingFiles = [],
  setExistingFiles,
  uploadedFiles,
  setUploadedFiles,
  onFileView,
}: PromptFileUploadProps): JSX.Element => {
  const handleRemoveUploadedFile = useCallback(
    (index: number) => {
      setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
    },
    [setUploadedFiles]
  );

  const handleRemoveExistingFile = useCallback(
    (index: number) => {
      setExistingFiles((prev) => {
        const updated = prev.filter((_, i) => i !== index);
        return updated;
      });
    },
    [setExistingFiles]
  );

  return (
    <Box style={customStyles}>
      <UploadTitle variant="subtitle2">Starter Files</UploadTitle>
      <FileDropzone
        label="Click to upload or drag and drop"
        hint="DOCX, PDF, Word, Txt (max. 25MB)"
        onFilesAdded={(files: File[]) => setUploadedFiles((prev) => [...prev, ...files])}
      />
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 4,
          mt: 3,
          paddingBottom: 2,
        }}
      >
        {existingFiles.length > 0 && (
          <FileListContainer>
            <FileListTitle variant="body1">Your current files</FileListTitle>
            <ExistingFilesList
              files={existingFiles}
              onDelete={handleRemoveExistingFile}
              onView={onFileView}
            />
          </FileListContainer>
        )}

        {uploadedFiles.length > 0 && (
          <FileListContainer>
            <FileListTitle variant="body1">Newly added files</FileListTitle>
            <UploadedFilesList files={uploadedFiles} onDelete={handleRemoveUploadedFile} />
          </FileListContainer>
        )}
      </Box>
    </Box>
  );
};

export default memo(PromptFileUpload);
